import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Users, Coins, Share2, Clock } from "lucide-react";
import { useI18n } from "@/i18n";

export const ReferralCard = () => {
  const [data, setData] = useState(null);
  const { t } = useI18n();

  useEffect(() => {
    api.get("/referral").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const link = `${window.location.origin}/?ref=${data.referral_code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("referral.copied"));
    } catch {
      toast.error(t("referral.copyFail"));
    }
  };

  const share = async () => {
    const text = t("referral.shareText", { n: data.signup_bonus });
    if (navigator.share) {
      try { await navigator.share({ title: "IMPERIUM Automotive Files", text, url: link }); } catch {}
    } else {
      copy();
    }
  };

  return (
    <div data-testid="referral-card" className="glass rounded-md p-6 mb-8 relative overflow-hidden">
      <div className="absolute -right-10 -top-10 w-48 h-48 bg-aurora-violet/10 blur-3xl rounded-full" />
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 grid place-items-center border border-aurora-violet/40 rounded-md bg-aurora-violet/10">
            <Gift className="w-6 h-6 text-aurora-violet" />
          </div>
          <div>
            <h3 className="font-display text-xl font-semibold uppercase tracking-tight">{t("referral.title")}</h3>
            <p className="text-zinc-400 text-sm">
              {t("referral.desc1")} <span className="text-aurora-cyan font-semibold">{data.signup_bonus} {t("referral.creditsWord")}</span> {t("referral.desc2")} <span className="text-aurora-cyan font-semibold">{data.reward_per_referral} {t("referral.creditsWord")}</span> {t("referral.desc3")}
            </p>
          </div>
        </div>

        <div className="flex items-stretch gap-2 mb-4">
          <input
            data-testid="referral-link-input"
            readOnly
            value={link}
            className="flex-1 bg-black/40 border border-white/10 rounded-sm px-3 py-2.5 font-mono text-xs text-zinc-300 truncate focus:outline-none"
          />
          <button onClick={copy} data-testid="referral-copy-btn" className="px-3 grid place-items-center border border-white/10 rounded-sm hover:bg-white/5 transition-colors" title="Copier">
            <Copy className="w-4 h-4 text-aurora-cyan" />
          </button>
          <button onClick={share} data-testid="referral-share-btn" className="px-3 grid place-items-center border border-white/10 rounded-sm hover:bg-white/5 transition-colors" title="Partager">
            <Share2 className="w-4 h-4 text-aurora-cyan" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-black/30 rounded-sm p-3 border border-white/5">
            <Users className="w-4 h-4 text-aurora-violet mb-2" />
            <div data-testid="referral-total" className="font-display text-2xl font-bold">{data.total_referred}</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">{t("referral.filleuls")}</div>
          </div>
          <div className="bg-black/30 rounded-sm p-3 border border-white/5">
            <Coins className="w-4 h-4 text-aurora-cyan mb-2" />
            <div data-testid="referral-earned" className="font-display text-2xl font-bold text-aurora-cyan">{data.credits_earned}</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">{t("referral.creditsEarned")}</div>
          </div>
          <div className="bg-black/30 rounded-sm p-3 border border-white/5">
            <Clock className="w-4 h-4 text-yellow-300 mb-2" />
            <div data-testid="referral-pending" className="font-display text-2xl font-bold text-yellow-300">{data.pending}</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">{t("referral.pending")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
